import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { getCargoGazetters, deleteCargoGazetter } from 'actions/cargoes/gazetters';
import Pagination from 'components/Libs/Pagination';
import * as materialUi from 'material-ui';
import AwaitableBtn from 'components/Base/AwaitableBtn';
import * as gazetteersApi from 'services/gazetters';
import { UserPerms } from 'store/configureStore';
import styles from 'styles';
import CargoEditorRow from './CargoEditorRow';

const rowsPerPage = [50, 100];

function CargoEditorTable({
    getCargoGazetters,
    deleteCargoGazetter,
    _items = [], 
    _total,
    _details, 
    _search, 
    _onlyEmpty,
    _page, 
    _limit,
    isLoaded,
}) {
    const [search, setSearch] = useState(_search || undefined);
    const [searchApplied, setSearchApplied] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [details, setDetails] = useState(undefined);
    const [rows, setRows] = useState(_items);
    const [total, setTotal] = useState(0);
    const [onlyEmpty, setOnlyEmpty] = useState(false);
    const [pendingUpdatesCount, setPendingUpdatesCount] = useState(0);
    const [showNotUpdatedDialog, setShowNotUpdatedDialog] = useState(false);
    const [tempNav, setTempNav] = useState({});

    useEffect(() => {
        setPage(1);
        getCargoGazetters({ page: 1, limit, filters: { search, details, onlyEmpty } });
    }, [onlyEmpty]);

    const searchGazetters = ({ newPage = 1, newLimit = 50, newSearchApplied = false }) => {
        getCargoGazetters({ 
            page: newPage, 
            limit: newLimit, 
            filters: { 
                search: newSearchApplied ? search : undefined, 
                details, 
                onlyEmpty,
            },
        });
    };

    const handleSearchClick = () => {
        setPage(1);
        setSearchApplied(!!search);
        searchGazetters({ newSearchApplied: !!search });
    };

    useEffect(() => {
        setTotal(_total);
        setRows(_items);
        setDetails(_details);
    }, [_total, _items, _page, _limit, _details]);

    const handleUpdateRow = (data, opts = {}) => {
        if (pendingUpdatesCount && !opts.force) {
            setShowNotUpdatedDialog(true);
            setTempNav({
                page: data.page, 
                numberOfRows: data.numberOfRows,
            });
            return;
        }

        if (!data) {
            data = tempNav;
            setShowNotUpdatedDialog(false);
            setTempNav({});
        }

        setLimit(data.numberOfRows);
        setPage(data.page);
        searchGazetters({ 
            newPage: data.page, 
            newLimit: data.numberOfRows,
            newSearchApplied: searchApplied,
        });
    };

    const syncGazetteersToDbEntries = () => {
        const answer = prompt('This operation will block gazetteer resources while running and may take up to a minute!\nType \'sync\' to confirm action');
        if (answer === 'sync') {
            return gazetteersApi.syncFeaturesFromDb();
        }

        return null;
    };

    const removeGazetter = (itemId) => {
        deleteCargoGazetter(itemId);
        searchGazetters();
    };

    /**
     * @param {'-' | '+'} sign
     */
    const changePendingUpdates = (sign) => {
        const currentCount = pendingUpdatesCount;
        if (sign === '-') {
            currentCount && setPendingUpdatesCount(currentCount - 1);
        } else {
            setPendingUpdatesCount(currentCount + 1);
        }
    };

    const navigateActions = [
        <materialUi.FlatButton
            label="Cancel"
            primary
            onClick={() => setShowNotUpdatedDialog(!showNotUpdatedDialog)}
        />,
        <materialUi.FlatButton
            label="Submit"
            primary
            keyboardFocused
            onClick={() => handleUpdateRow(undefined, { force: true })}
        />,
    ];

    const updateRowsRealCargoData = (newRealCargoData) => {
        const _rows = _.cloneDeep(rows);

        _rows.forEach((row) => {
            if (row.realCargoId === newRealCargoData._id) {
                row.realCargoName = newRealCargoData.name;
                row.details = newRealCargoData.details;
                row.CargoCategoryName = newRealCargoData.parent.red.name;
                row.CargoCategory = newRealCargoData.parent.red._id;
            }
        });

        setRows(_rows);
    };

    return (
        <div>
            <materialUi.Dialog
                title=''
                open={showNotUpdatedDialog}
                actions={navigateActions}
                onRequestClose={() => setShowNotUpdatedDialog(!showNotUpdatedDialog)}
            >
                Are you sure you want to navigate? Some cargoes are pending updates.
            </materialUi.Dialog>
            <div className='table-header' style={{ justifyContent: 'space-between' }}>
                <div>
                    <materialUi.TextField
                        defaultValue={search || ''}
                        floatingLabelText='Search'
                        onChange={e => setSearch(e.target.value || undefined)}
                        onKeyDown={e => e.keyCode === 13 && handleSearchClick()}
                    />
                    <materialUi.Checkbox
                        label='Empty fields'
                        checked={onlyEmpty}
                        onCheck={() => setOnlyEmpty(!onlyEmpty)}
                        style={{ fontSize: '13px' }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <materialUi.RaisedButton
                        label='Search'
                        onClick={handleSearchClick}
                        primary
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <AwaitableBtn>
                        <materialUi.RaisedButton
                            label='Sync cargo features'
                            disabled={!UserPerms.isAdmin()}
                            onClick={syncGazetteersToDbEntries}
                            primary
                        />
                    </AwaitableBtn>
                </div>
                <div>
                    <Pagination
                        total={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        numberOfRows={limit}
                        updateRows={handleUpdateRow}
                    />
                </div>
            </div>
            <div className='table-body'>
                <materialUi.Table selectable={false}>
                    <materialUi.TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <materialUi.TableRow>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnCSS}>
                                Cargo name
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnCSS}>
                                Real Cargo name
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnCSS}>
                                Cargo type
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnCSS}>
                                Last modified
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnSmallCSS}>
                                Details
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnCSS}>
                                By user
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={styles.tableRowColumnSmallCSS}>
                                {null}
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, width: '48px' }}>
                                {null}
                            </materialUi.TableHeaderColumn>
                            <materialUi.TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, width: '48px' }}>
                                {null}
                            </materialUi.TableHeaderColumn>
                        </materialUi.TableRow>
                    </materialUi.TableHeader>

                    <materialUi.TableBody 
                        className={isLoaded ? '' : 'hidden'}
                        displayRowCheckbox={false}
                    >
                        {
                            isLoaded ? 
                                rows.map(item => (
                                    <CargoEditorRow
                                        key={item._id}
                                        item={item}
                                        removeGazetter={removeGazetter}
                                        changePendingUpdates={changePendingUpdates}
                                        updateRowsRealCargoData={updateRowsRealCargoData}
                                    />
                                )) :
                                <materialUi.CircularProgress
                                    style={styles.circularProgress}
                                />
                        }
                    </materialUi.TableBody>
                </materialUi.Table>
            </div>
        </div>
    );
}

export default connect(
    (state) => {
        const {
            items, total, filters, page, limit, isLoaded,
        } = state.cargoGazetters;

        return {
            _items: items || [], 
            _total: total,
            _details: filters ? filters.details : undefined, 
            _search: filters ? filters.search : undefined, 
            _onlyEmpty: filters ? filters.onlyEmpty : false,
            _page: page, 
            _limit: limit || 50,
            isLoaded,
        };
    },
    dispatch => bindActionCreators(
        {
            getCargoGazetters,
            deleteCargoGazetter,
        },
        dispatch,
    ),
)(CargoEditorTable);



// WEBPACK FOOTER //
// ./app/components/Cargoes/Assigners/CargoEditorTable.jsx